import PageLoader from "Utils/PageLoader";
import Guid from "Utils/Guid";
import { PubSub, Subscriptions } from "Subscriptions";
import ClientSession from "ClientSession";


const defaults = {
    basePath: "/api/"
};

/**
 * Class Rest
 * 
 * This class is a wrapper around jQuery's Ajax.  It handles
 * errors and returns A+ compliant promises
 * 
 * Properties:
 * 
 * Methods:
 * GET: Executes a GET call using the provided URL and data
 * POST: Executes a POST call using the provided URL and data
 * PUT: Executes a PUT call using the provided URL and data
 * DELETE: Executes a DELETE call using the provided URL and data
 * 
 */


export type AjaxOptions = {
    dataType?;
    requestId?;
    shouldShowOverlay?: boolean;
};

export default class BaseRest {
    private basePath: string;
    private isExternal: boolean;
    private url: string;
    private data: Object;
    private options: Object;

    constructor(options) {
        this.basePath = options.basePath || defaults.basePath;
        this.isExternal = options.isExternal;
    }

    /**
     * Execute a GET call
     * 
     * @param url The URL to reach
     * @param data Data to be passed to the AJAX call
     */
    GET<T>(url: string, data: Object, options?: any) {
        var promise = this.executeAjax<T>("GET", url, data, options);
        return promise;
    }

    /**
     * Execute a POST call
     * 
     * @param url The URL to reach
     * @param data Data to be passed to the AJAX call
     */
    POST<T>(url: string, data: Object, options?: any) {
        var promise = this.executeAjax<T>("POST", url, data, options);
        return promise;
    }

    /**
     * Execute a PUT call
     * 
     * @param url The URL to reach
     * @param data Data to be passed to the AJAX call
     */
    PUT<T>(url: string, data: Object, options?: any) {
        var promise = this.executeAjax<T>("PUT", url, data, options);
        return promise;
    }

    /**
     * Execute a DELETE call
     * 
     * @param url The URL to reach
     * @param data Data to be passed to the AJAX call
     */
    DELETE<T>(url: string, data: Object, options?: any) {
        var promise = this.executeAjax<T>("DELETE", url, data, options);
        return promise;
    }

    /**
     * This function executes a REST call and then returns a 
     * promise.  All errors will be handled by the general handler
     * functions and the promise will then be rejected.
     */
    
    private executeAjax<T>(callType: string, url: string, data: Object, options?: any) {
        //Format the URL using the basePath
        this.url = this.formatUrl(this.basePath + url);
        this.data = data;
        this.options = options;

        var dataType = 'json_with_dates';
        var shouldShowOverlay = false;
        var requestId;

        if (typeof options !== 'undefined') {
            if (typeof options.dataType !== 'undefined') {
                dataType = options.dataType;
            }
            if (typeof options.requestId !== 'undefined') {
                requestId = options.requestId;
            }

            shouldShowOverlay = options.shouldShowOverlay || false;
        }

        var pageLoaderId;

        if (shouldShowOverlay) {
            pageLoaderId = Guid.createGuid();
            PageLoader.trackRequest(pageLoaderId, false);
        }

        Promise.onPossiblyUnhandledRejection(reason => {
            // Note - We do nothing here. This is to keep BlueBird from spamming the console when bad requests occur
        });
        //Create a promise that will be resolved or rejected upon
        //success/failure
        var promise = new Promise<T>((resolve, reject, onCancel) => {

            var ajaxOptions = {
                url: this.url,
                data: null,
                contentType: null,
                success: data => {
                    if (pageLoaderId) {
                        PageLoader.trackRequest(pageLoaderId, true);
                    }

                    if (requestId) {
                        resolve(<any>{ data: data, requestId: requestId });
                    } else {
                        resolve(data);
                    }
                },
                error: err => {
                    if (pageLoaderId) {
                        PageLoader.trackRequest(pageLoaderId, true);
                    }

                    if (requestId) {
                        reject(<any>{ err: err, requestId: requestId });
                    } else {
                        reject(err);
                    }
                },
                type: callType,
                dataType: dataType
            };

            if (callType == 'GET') {
                ajaxOptions.data = this.data;
            } else {
                ajaxOptions.data = JSON.stringify(this.data);
                ajaxOptions.contentType = 'application/json';
            }

            var xhr = $.ajax(ajaxOptions);

            onCancel(() => {
                xhr.abort();
            });

        });

        //If the AJAX call was a success, resolve the promise with the returned value
        //If it failed, process the error and then reject the promise.
        promise.then(val => {
            return val;
        }, err => {
            //Add general handlers here
            var error = err.err ? err.err : err;
            switch (error.status) {
                case 401:
                    if(ClientSession.redirectOn401)
                        PubSub.publish(Subscriptions.App.RedirectPage, 'Security/Login?reason=nosession');
                    break;
                case 404:
                    this.general404Handler(error);
                    break;
                default:
                    this.generalErrorHandler(error);
            }
            return error;
        });

        return promise;
    }

    /**
     * Format the URL, right now it only removes the double
     * slashes that could be accidently added
     * @param url string The URL to reformat
     * @return string A formatted URL
     */
    private formatUrl(url) {
        //Remove double slashes
        if (!this.isExternal) {
            url = url.replace("//", "/");
        }

        return url;
    }

    /**
     * Private function that processes the 404 errors
     */
    private general404Handler(err: any) {
    }

    /**
     * Private function that processes generic errors
     */
    private generalErrorHandler(err: any) {
        // Note - Right now we do nothing here... We could log the error from here if we so please
    }
}
